import { Dayjs } from "dayjs";

import { PHASE_SLOT_NAMES } from "../../../../constants/ripeningPhaseSlots";
import { RipeningRoomRun, useRipeningRoomRun } from "../../../../hooks/useRipeningPrograms";
import { dayjs } from "../../../../utils/date";
import useRipeningRoom from "../hooks/useRipeningRoom";
import { getRipeningProgramDisplayValue } from "./ripeningProgramCycle";
import { getCycleStore } from "./runningProgram";

// * How long a start the room confirmed is shown as running before its own status catches up.
export const ACCEPTED_START_GRACE_MS = 10 * 60 * 1000;

// * Run states in which a program is loaded in the room (off / unknown → nothing to show).
const RUNNING_STATES = new Set(["ripening", "storage", "paused"]);

// * The gateway prefixes the program name it writes to the room; clients never see that prefix.
const PROGRAM_NAME_PREFIX = /^\s*ripewise\s*-\s*/i;

export function getRunProgramName(run: RipeningRoomRun | null | undefined): string | null {
    const name = typeof run?.recipe === "string" ? run.recipe.replace(PROGRAM_NAME_PREFIX, "").trim() : "";
    return name || null;
}

export function getRunPhaseName(run: RipeningRoomRun | null | undefined): string | null {
    // * Phase names are AVOS configuration: the service only reports the number and the
    // * controller's own name.
    const configured = typeof run?.phase_number === "number" ? PHASE_SLOT_NAMES[run.phase_number - 1] : undefined;
    if (configured) return configured;
    if (run?.phase === null || run?.phase === undefined) return null;
    const phase = String(run.phase).trim();
    if (!phase) return null;
    // * A bare number carries no meaning on its own.
    return /^\d+$/.test(phase) ? `Phase ${phase}` : phase;
}

const percent = (value: number) => Math.min(100, Math.max(0, Math.round(value)));

/**
 * Progress of the whole program as 0..100: the room's own figure ("57%" or 57), else the
 * time passed between the room's start and end; null when unknown.
 */
export function getRunProgress(run: RipeningRoomRun | null | undefined, now: Dayjs = dayjs()): number | null {
    if (typeof run?.progress_pct === "number" && Number.isFinite(run.progress_pct)) return percent(run.progress_pct);
    const value = typeof run?.progress === "number" ? run.progress : parseFloat(String(run?.progress ?? "").replace("%", ""));
    if (Number.isFinite(value)) return percent(value);
    const startAt = run?.start_at ? dayjs(run.start_at) : null;
    const endAt = run?.end_at ? dayjs(run.end_at) : null;
    if (!startAt?.isValid() || !endAt?.isValid() || !endAt.isAfter(startAt)) return null;
    return percent((now.diff(startAt) / endAt.diff(startAt)) * 100);
}

/** Progress of the current phase as 0..100 (% of its duration); null when the room does not report it. */
export function getRunPhaseProgress(run: RipeningRoomRun | null | undefined): number | null {
    return typeof run?.phase_progress_pct === "number" && Number.isFinite(run.phase_progress_pct) ? percent(run.phase_progress_pct) : null;
}

export function isRunning(run: RipeningRoomRun | null | undefined): boolean {
    return !!run?.state && RUNNING_STATES.has(String(run.state).toLowerCase());
}

/** The room's live status, with a start the room just confirmed shown as running until
 *  the room's own status catches up. `run` is null when the status is unavailable. */
export function useRipeningRoomLiveRun(gatewayId?: string | null, roomId?: string | null, tenantId?: string | null) {
    const { cycleStartTracker, activeRipeningCycle } = useRipeningRoom();
    const cycleStart = cycleStartTracker?.cycleStart;
    const query = useRipeningRoomRun(gatewayId, roomId, { tenantId }, {
        startPending: cycleStart?.status === "pending",
    });
    const liveRun = query.isError ? null : query.data?.run;
    // * The room confirmed the start but its status has not caught up yet: show the cycle's
    // * program as running already.
    const justStarted = !isRunning(liveRun)
        && cycleStart?.status === "accepted"
        && cycleStart.cycleId === activeRipeningCycle?.id
        && !!cycleStart.acceptedAt
        && Date.now() - cycleStart.acceptedAt < ACCEPTED_START_GRACE_MS;
    const startedProgramName = justStarted ? getRipeningProgramDisplayValue(activeRipeningCycle?.meta?.ripening_program) : null;
    const run: RipeningRoomRun | null | undefined = justStarted
        ? {
            // * A storage program runs as a store, not as a ripening program.
            state: getCycleStore(activeRipeningCycle?.meta) ? "storage" : "ripening",
            recipe: typeof startedProgramName === "string" ? startedProgramName : null,
        }
        : liveRun;
    return { ...query, run, justStarted };
}
